<script setup lang="ts">
import { CheckCircle2, Loader2, XCircle } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { useRunProgress } from '@/composables/useRunProgress';

const props = defineProps<{
    open: boolean;
    runId: string | null;
    projectId: string;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
    done: [];
}>();

interface ImportedPaper {
    paper_id: string;
    title: string;
}

const importedPapers = ref<ImportedPaper[]>([]);
const progressInfo = ref<{
    current: number;
    total: number;
    percentage: number;
} | null>(null);
const errorMessage = ref<string | null>(null);
const polledStatus = ref<string | null>(null);
let runProgress: ReturnType<typeof useRunProgress> | null = null;

watch(
    () => props.runId,
    (id) => {
        if (!id) {
            return;
        }

        importedPapers.value = [];
        progressInfo.value = null;
        errorMessage.value = null;

        runProgress = useRunProgress(id);

        // Watch run events for paper_imported and progress
        watch(
            () => runProgress!.events.value,
            (events) => {
                const papers = events
                    .filter((ev) => ev.type === 'RunProgressUpdated')
                    .flatMap(() => []);
                // Pull from poll instead — useRunProgress doesn't separate event types
                // We'll poll directly for paper details
                void papers;
            },
            { deep: true },
        );
    },
    { immediate: true },
);

// Separate polling for paper details (since useRunProgress only tracks run-level events)
const pollInterval = ref<ReturnType<typeof setInterval> | null>(null);

watch(
    () => props.open,
    (open) => {
        if (open && props.runId) {
            startPolling();
        } else {
            stopPolling();
        }
    },
);

watch(
    () => runProgress?.status.value,
    (s) => {
        if (s && s !== 'running' && s !== 'pending') {
            stopPolling();
        }
    },
);

function startPolling() {
    stopPolling();
    pollInterval.value = setInterval(fetchRunDetails, 2000);
    void fetchRunDetails();
}

function stopPolling() {
    if (pollInterval.value) {
        clearInterval(pollInterval.value);
        pollInterval.value = null;
    }
}

async function fetchRunDetails() {
    if (!props.runId) {
        return;
    }

    try {
        const res = await fetch(`/runs/${props.runId}`, {
            headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
            return;
        }

        const run = await res.json();

        const events: { type: string; payload: Record<string, unknown> }[] =
            run.events ?? [];

        const progressEvents = events.filter(
            (ev) => ev.type === 'import_progress',
        );

        if (progressEvents.length > 0) {
            const last = progressEvents.at(-1)!;
            progressInfo.value = {
                current: (last.payload.completed as number) ?? 0,
                total: (last.payload.total as number) ?? 0,
                percentage: (last.payload.percentage as number) ?? 0,
            };
        }

        importedPapers.value = events
            .filter((ev) => ev.type === 'paper_imported')
            .map((ev) => ({
                paper_id: ev.payload.paper_id as string,
                title: (ev.payload.title as string) ?? 'Sans titre',
            }));

        if (run.status) {
            polledStatus.value = run.status;

            if (run.status !== 'pending' && run.status !== 'running') {
                stopPolling();
            }
        }

        if (run.error) {
            errorMessage.value = run.error;
        }
    } catch {
        // silence network errors during polling
    }
}

const status = () =>
    polledStatus.value ?? runProgress?.status.value ?? 'running';
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent side="right" class="flex w-96 flex-col gap-0 p-0">
            <SheetHeader class="border-b border-border/40 px-5 py-4">
                <SheetTitle class="text-base"
                    >Import Zotero en cours</SheetTitle
                >
                <SheetDescription class="text-xs">
                    Les références sont importées en arrière-plan.
                </SheetDescription>
            </SheetHeader>

            <!-- Status banner -->
            <div
                class="flex shrink-0 items-center gap-3 border-b border-border/40 px-5 py-3 text-sm"
                :class="{
                    'bg-amber-50 text-amber-700':
                        status() === 'running' || status() === 'pending',
                    'bg-emerald-50 text-emerald-700': status() === 'completed',
                    'bg-rose-50 text-rose-700': status() === 'failed',
                }"
            >
                <Loader2
                    v-if="status() === 'running' || status() === 'pending'"
                    class="h-4 w-4 shrink-0 animate-spin"
                />
                <CheckCircle2
                    v-else-if="status() === 'completed'"
                    class="h-4 w-4 shrink-0"
                />
                <XCircle v-else class="h-4 w-4 shrink-0" />

                <div class="min-w-0">
                    <template
                        v-if="status() === 'running' || status() === 'pending'"
                    >
                        <span v-if="progressInfo">
                            Importation : {{ progressInfo.current }} /
                            {{ progressInfo.total }} article{{
                                progressInfo.total > 1 ? 's' : ''
                            }}
                        </span>
                        <span v-else>Importation en cours…</span>
                    </template>
                    <template v-else-if="status() === 'completed'">
                        {{ importedPapers.length }} article{{
                            importedPapers.length > 1 ? 's' : ''
                        }}
                        importé{{ importedPapers.length > 1 ? 's' : '' }} avec
                        succès.
                    </template>
                    <template v-else>
                        {{ errorMessage ?? "Erreur lors de l'import." }}
                    </template>
                </div>
            </div>

            <!-- Progress bar -->
            <div
                v-if="
                    progressInfo &&
                    (status() === 'running' || status() === 'pending')
                "
                class="h-1 shrink-0 bg-muted"
            >
                <div
                    class="h-full bg-foreground transition-all duration-500"
                    :style="{ width: `${progressInfo.percentage}%` }"
                />
            </div>

            <!-- Imported papers list -->
            <ScrollArea class="min-h-0 flex-1">
                <ul class="divide-y divide-border/40">
                    <li
                        v-for="paper in [...importedPapers].reverse()"
                        :key="paper.paper_id"
                        class="px-5 py-3"
                    >
                        <p
                            class="line-clamp-2 text-xs leading-snug text-foreground"
                        >
                            {{ paper.title }}
                        </p>
                    </li>
                    <li
                        v-if="importedPapers.length === 0"
                        class="px-5 py-8 text-center text-xs text-muted-foreground italic"
                    >
                        Les articles importés apparaîtront ici.
                    </li>
                </ul>
            </ScrollArea>

            <!-- Footer -->
            <div class="shrink-0 border-t border-border/40 px-5 py-4">
                <Button
                    v-if="status() !== 'running' && status() !== 'pending'"
                    class="w-full"
                    size="sm"
                    @click="emit('done')"
                >
                    Voir dans Articles
                </Button>
                <p v-else class="text-center text-xs text-muted-foreground">
                    Vous pouvez fermer ce panneau. L'import continuera en
                    arrière-plan.
                </p>
            </div>
        </SheetContent>
    </Sheet>
</template>
